/**
 * Axios configuration with interceptors
 * Adds authentication (Bearer token) and handles 401 errors
 *
 * Follows the pattern: API Integration Architecture
 */

import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retried?: boolean };

/**
 * Hook that creates and configures an Axios instance with interceptors
 * - Adds Bearer token to all requests
 * - Handles 401 errors: triggers a session refresh and retries the request ONCE with the fresh
 *   token; if no fresh token arrives in time, rejects (no further retries here or at the query
 *   layer — TanStack Query's retryUnless401 already refuses to retry a 401 itself, and this must
 *   stay a single bounded attempt, not a loop, to avoid a 401 storm on initial mount before auth
 *   completes). checkSession()'s own fail-closed logic is what eventually navigates to /login when
 *   the session genuinely cannot be refreshed — this hook does not duplicate that.
 *
 * @returns Configured Axios instance
 */
export const useAxios = (): AxiosInstance => {
  const { accessTokenRef, checkSession, waitForNextToken } = useAuth();

  const axiosInstance = axios.create({
    headers: {
      'Content-Type': 'application/json',
    },
  });

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = accessTokenRef.current;
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
      }
      config.headers.set('app-code', 'GLOBAL_CONTAINER_APP');
      return config;
    },
    (error) => {
      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const config = error.config as RetryableRequestConfig | undefined;

      if (error.response?.status === 401) {
        console.error('Invalid or expired token');

        if (config == null || config._retried === true) {
          return Promise.reject(error);
        }

        // The token actually used on the request that just failed — the ground truth to detect
        // whether accessTokenRef has already moved past it.
        const tokenAtFailure = config.headers.get('Authorization');

        config._retried = true;
        // Started unconditionally so it's already listening before checkSession() can resolve
        // synchronously-fast; deliberately left unawaited below on the already-rotated
        // short-circuit path — it still settles in the background and is just discarded.
        const waitPromise = waitForNextToken();
        checkSession();

        const currentToken = accessTokenRef.current;
        const currentAuthHeader = currentToken ? `Bearer ${currentToken}` : null;

        // The token may have already rotated (e.g. a proactive refresh already in flight) before
        // this 401 was even processed. waitForNextToken() can only observe a FUTURE change, so
        // short-circuit here instead of waiting for an event that already happened.
        if (
          tokenAtFailure != null &&
          currentAuthHeader != null &&
          currentAuthHeader !== tokenAtFailure
        ) {
          return axiosInstance.request(config);
        }

        const gotFreshToken = await waitPromise;
        if (gotFreshToken === true) {
          return axiosInstance.request(config);
        }
      }

      return Promise.reject(error);
    },
  );

  return axiosInstance;
};
